import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { EntityCollectionServiceElementsFactory } from '@ngrx/data';
import { ampli, SiteInterface, TeamInterface } from '@shared/api';
import { environment } from '@shared/environments';
import {
  FormButtonTypeEnum,
  FormComponent,
  FormControlTypeEnum,
  FormService,
} from '@shared/form';
import { IcuLocalizationService } from '@shared/services';
import { isArrayOfStrings } from '@shared/utils';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CustomEntityCollectionBaseService } from '../../entity-collection-base.class';
import { EntityTypeEnum } from '../../entity.interface';
import { TeamsService } from '../teams.service';

@Injectable({
  providedIn: 'root',
})
export class TeamSitesService extends CustomEntityCollectionBaseService<SiteInterface> {
  private readonly http = inject(HttpClient);
  private readonly formService = inject(FormService);
  private readonly toastrService = inject(ToastrService);
  private readonly teamsService = inject(TeamsService);
  private readonly icuLocalizationService = inject(IcuLocalizationService);

  constructor(elementsFactory: EntityCollectionServiceElementsFactory) {
    super(EntityTypeEnum.TeamSite, elementsFactory);
  }

  openRevokeAccessModal(
    teamSites: SiteInterface[],
    teamId: string,
  ): MatDialogRef<FormComponent> {
    return this.formService.openForm({
      data: {
        title: $localize`:@@teamSitesRevokeAccessModalTitle:Revoke access?`,
        form: {
          ids: ['label'],
          controls: {
            label: {
              title:
                teamSites.length > 1
                  ? this.icuLocalizationService.format(
                      $localize`:@@teamSitesMultipleRevokeAccessModalLabel:Revoke access to {count, plural, one {<b>#</b> site} other {<b>#</b> sites}} for the team`,
                      { count: teamSites.length },
                    )
                  : $localize`:@@teamSitesSingleRevokeAccessModalLabel:Revoke access to the site <b>${teamSites[0]!.name}</b> for the team`,
              type: FormControlTypeEnum.LABEL,
            },
          },
        },
        actions: [
          {
            type: FormButtonTypeEnum.SECONDARY,
          },
          {
            type: FormButtonTypeEnum.PRIMARY,
            action: () =>
              this.revokeAccess(teamSites, teamId).pipe(
                tap(() => {
                  this.toastrService.success(
                    teamSites.length > 1
                      ? this.icuLocalizationService.format(
                          $localize`:@@teamSitesMultipleRevokeAccessSuccessNotification:You have revoked access to {count, plural, one {<b>#</b> site} other {<b>#</b> sites}} for the team`,
                          { count: teamSites.length },
                        )
                      : $localize`:@@teamSitesSingleRevokeAccessSuccessNotification:You have revoked access to the site <b>${teamSites[0]!.name}</b> for the team`,
                  );
                }),
              ),
          },
        ],
      },
    });
  }

  openTurnOnAutoGrantAccessModal(
    team: TeamInterface,
  ): MatDialogRef<FormComponent> {
    return this.formService.openForm({
      data: {
        title: $localize`:@@teamSitesTurnOnAutoGrantAccessModalTitle:Turn on auto-grant access to sites?`,
        form: {
          ids: ['label', 'info'],
          controls: {
            label: {
              title: $localize`:@@teamSitesTurnOnAutoGrantAccessModalText:Grant access to all sites to the team <b>${team.name}</b>`,
              type: FormControlTypeEnum.LABEL,
            },
            info: {
              title: $localize`:@@teamSitesTurnOnAutoGrantAccessModalInfo:You can turn off this feature at any time and grant access to your sites manually`,
              type: FormControlTypeEnum.INFO_WARNING,
            },
          },
        },
        actions: [
          {
            type: FormButtonTypeEnum.SECONDARY,
          },
          {
            type: FormButtonTypeEnum.PRIMARY,
            action: () =>
              this.teamsService.turnOnAutoGrantAccess(team).pipe(
                tap(() => {
                  ampli.siteAutoSharingChanged({
                    team_id: team.guid,
                    access_state: 'on',
                  });
                  this.toastrService.success(
                    $localize`:@@teamSitesTurnOnAutoGrantAccessSuccessNotification:You have turned on auto-grant access for the team <b>${team.name}</b>`,
                  );
                }),
              ),
          },
        ],
      },
    });
  }

  openTurnOffAutoGrantAccessModal(
    team: TeamInterface,
  ): MatDialogRef<FormComponent> {
    return this.formService.openForm({
      data: {
        title: $localize`:@@teamSitesTurnOffAutoGrantAccessModalTitle:Turn off auto-grant access to sites?`,
        form: {
          ids: ['label'],
          controls: {
            label: {
              title: $localize`:@@teamSitesTurnOffAutoGrantAccessModalText:Revoke access to all sites for the team <b>${team.name}</b>`,
              type: FormControlTypeEnum.LABEL,
            },
          },
        },
        actions: [
          {
            type: FormButtonTypeEnum.SECONDARY,
          },
          {
            type: FormButtonTypeEnum.PRIMARY,
            action: () =>
              this.teamsService.turnOffAutoGrantAccess(team).pipe(
                tap(() => {
                  ampli.siteAutoSharingChanged({
                    team_id: team.guid,
                    access_state: 'off',
                  });
                  this.toastrService.success(
                    $localize`:@@teamSitesTurnOffAutoGrantAccessSuccessNotification:You have turned off auto-grant access for the team <b>${team.name}</b>`,
                  );
                }),
              ),
          },
        ],
      },
    });
  }

  confirmGrantAccess(
    teamGuid: string,
    selectedItems: SiteInterface[] | string[],
  ): Observable<unknown> {
    return this.addSite(teamGuid, selectedItems).pipe(
      tap(() =>
        ampli.siteSharingChanged({
          site_id_array: isArrayOfStrings(selectedItems)
            ? (selectedItems as string[])
            : (selectedItems as SiteInterface[]).map((site) => site.guid),
          site_state: 'added',
          team_id: teamGuid,
        }),
      ),
    );
  }

  addSite(
    teamGuid: string,
    selectedItems: SiteInterface[] | string[],
  ): Observable<unknown> {
    const siteGuids = isArrayOfStrings(selectedItems)
      ? (selectedItems as string[])
      : selectedItems.map((item) => (item as SiteInterface).guid);

    return this.http
      .post<unknown>(`${environment.apiHost}/team_v2/${teamGuid}/add-site`, {
        siteGuids: siteGuids,
      })
      .pipe(tap(() => this.refresh()));
  }

  revokeAccess(
    teamSites: SiteInterface[],
    teamId: string,
  ): Observable<unknown> {
    return this.delete(teamSites).pipe(
      tap(() =>
        ampli.siteSharingChanged({
          site_id_array: teamSites.map((teamSite) => teamSite.guid),
          site_state: 'removed',
          team_id: teamId,
        }),
      ),
    );
  }
}
